import { useEffect, useState } from 'react';
import { Box, Card, CardBody, CardHeader, DataTable } from 'grommet';
import { Configure } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import {
  DataTableHeader,
  DataTableItem,
  FooterNav,
  HeaderNav,
  LoadingSpinner,
  RevenueByReportingPeriodChart,
  Text,
} from '/src/components';
import AdminSideNav from '/src/components/admin/AdminSideNav';
import { useDashboardStore } from '/src/context';

const FuseAdminDashboard = observer(() => {
  const dashboardStore = useDashboardStore();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const fetchDashboard = async () => {
      try {
        await dashboardStore.getAdminView();
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboard();
    return () => dashboardStore.clearDashboardData();
  }, []);

  interface ProgramData {
    name: string;
    price: string;
    updated: string;
  }

  let programData: ProgramData[] = [];
  if (dashboardStore.dashboardData) {
    const latestByProgram = new Map<string, { price: string; updated: string }>();

    dashboardStore.dashboardData.reporting_periods.forEach((period) => {
      period.programs.forEach((program) => {
        latestByProgram.set(program.name, {
          price: program.price_per_credit ?? '—',
          updated: period.reporting_period,
        });
      });
    });

    programData = Array.from(latestByProgram.entries()).map(([name, { price, updated }]) => ({
      name,
      price,
      updated,
    }));
  }

  return (
    <Box height={{ min: '100vh' }} background="light" style={{ display: 'flex', flexDirection: 'column' }}>
      <HeaderNav />
      <Box flex="grow" style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        <AdminSideNav />
        <Box flex="grow" pad={{ horizontal: 'large', vertical: 'medium' }} overflow="auto">
          <Box flex={false}>
            <Box
              direction="row"
              align="center"
              justify="between"
              margin={{ vertical: 'medium' }}
              pad={{ horizontal: 'small' }}
            >
              <Text size="xlarge" weight="normal">
                FuSE Admin
              </Text>
            </Box>

            <Card margin={{ vertical: 'large' }} background="white" elevation="none" round="xsmall">
              <CardHeader
                pad={{ horizontal: 'medium', vertical: 'medium' }}
                border={{ side: 'bottom', color: 'light-4', size: '1px' }}
              >
                <Text size="large" weight="normal">
                  Current Credit Pricing
                </Text>
              </CardHeader>
              <CardBody pad="none">
                <Box elevation="small">
                  {isLoading ? (
                    <Box height="200px">
                      <LoadingSpinner />
                    </Box>
                  ) : (
                    <DataTable
                      pad={{ horizontal: '1.5rem' }}
                      columns={[
                        {
                          property: 'name',
                          primary: true,
                          size: '60%',
                          header: <DataTableHeader toUpperCase>PROGRAM NAME</DataTableHeader>,
                          render: (datum: ProgramData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem icon={<Configure size="small" color="brand" />}>
                                {datum.name}
                              </DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'price',
                          size: '20%',
                          header: <DataTableHeader toUpperCase>PRICE</DataTableHeader>,
                          render: (datum: ProgramData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>${datum.price}</DataTableItem>
                            </Box>
                          ),
                        },
                        {
                          property: 'updated',
                          size: '20%',
                          header: <DataTableHeader toUpperCase>UPDATED</DataTableHeader>,
                          render: (datum: ProgramData) => (
                            <Box pad={{ vertical: '1rem' }}>
                              <DataTableItem>{datum.updated}</DataTableItem>
                            </Box>
                          ),
                        },
                      ]}
                      data={programData}
                      background={['light-6', 'white']}
                      border={{ color: 'light-2', side: 'bottom', size: 'small' }}
                    />
                  )}
                </Box>
              </CardBody>
            </Card>

            <Box>
              {!isLoading && dashboardStore.dashboardData && (
                <RevenueByReportingPeriodChart data={dashboardStore.dashboardData} />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box flex={false}>
        <FooterNav />
      </Box>
    </Box>
  );
});

export default FuseAdminDashboard;
